import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * The apps assets publish to (issues #217, #218). An app has no source
 * settings: what it serves for each asset is that asset's pin for it, set on
 * the asset's page, or d3's latest without one. Staff create, rename and delete
 * apps here; an app's CDN id never changes, because its consumers build their
 * URLs on it.
 */
class StaffAppsView extends BaseComponent {
    static properties = {
        _apps: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _saving: { type: Boolean, state: true },
        _newSlug: { type: String, state: true },
        _newName: { type: String, state: true },
        _editing: { type: Object, state: true },
        _deleting: { type: Object, state: true },
        _confirmSlug: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        .app-card { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1rem; }
        .app-card h6 { font-weight: 600; margin-bottom: 0.5rem; }
        .danger { border-color: #dc3545; }
    `;

    constructor() {
        super();
        this._apps = [];
        this._loading = true;
        this._error = null;
        this._saving = false;
        this._newSlug = '';
        this._newName = '';
        this._editing = null;
        this._deleting = null;
        this._confirmSlug = '';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/apps');
            this._apps = data.apps || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _run(request) {
        this._saving = true;
        this._error = null;
        try {
            await request();
            await this._load();
            return true;
        } catch (err) {
            this._error = err.message;
            return false;
        } finally {
            this._saving = false;
        }
    }

    async _create(e) {
        e.preventDefault();
        const body = { slug: this._newSlug, name: this._newName };
        if (await this._run(() => api.post('/api/apps', body))) {
            this._newSlug = '';
            this._newName = '';
        }
    }

    async _saveEdit() {
        const { id, slug, name } = this._editing;
        if (await this._run(() => api.put(`/api/apps/${id}`, { slug, name }))) {
            this._editing = null;
        }
    }

    async _confirmDelete() {
        const { id } = this._deleting;
        if (await this._run(() => api.delete(`/api/apps/${id}`))) {
            this._deleting = null;
            this._confirmSlug = '';
        }
    }

    render() {
        return html`
            <div class="page-header">
                <h4>Apps</h4>
            </div>
            <p class="page-subtitle">
                Each app serves d3's latest version of every asset unless the asset pins something else for it. Pins are set on each asset's page.
                An app's CDN id never changes: its consumers build their URLs on it.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : nothing}

            ${
                this._loading
                    ? html`<div class="text-center py-4"><div class="spinner-border text-primary"></div></div>`
                    : this._apps.map((app) => this._renderApp(app))
            }

            <form class="app-card d-flex gap-2 align-items-end" @submit="${this._create}">
                <div>
                    <label class="form-label small">Slug</label>
                    <input type="text" class="form-control form-control-sm" placeholder="e.g. vtt-stg" required
                        .value="${this._newSlug}" @input="${(e) => (this._newSlug = e.target.value)}">
                </div>
                <div>
                    <label class="form-label small">Name</label>
                    <input type="text" class="form-control form-control-sm" placeholder="e.g. VTT (Staging)" required
                        .value="${this._newName}" @input="${(e) => (this._newName = e.target.value)}">
                </div>
                <button class="btn btn-primary btn-sm" ?disabled="${this._saving}">Create app</button>
            </form>
        `;
    }

    _renderApp(app) {
        if (this._editing?.id === app.id) {
            return html`
                <div class="app-card">
                    <div class="d-flex gap-2 align-items-end mb-2">
                        <div>
                            <label class="form-label small">Slug</label>
                            <input type="text" class="form-control form-control-sm" .value="${this._editing.slug}"
                                @input="${(e) => (this._editing = { ...this._editing, slug: e.target.value })}">
                        </div>
                        <div>
                            <label class="form-label small">Name</label>
                            <input type="text" class="form-control form-control-sm" .value="${this._editing.name}"
                                @input="${(e) => (this._editing = { ...this._editing, name: e.target.value })}">
                        </div>
                        <button class="btn btn-primary btn-sm" ?disabled="${this._saving}" @click="${this._saveEdit}">Save</button>
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => (this._editing = null)}">Cancel</button>
                    </div>
                    <div class="small text-muted">CDN id <code>${app.cdn_id}</code> (doesn't change)</div>
                </div>
            `;
        }
        if (this._deleting?.id === app.id) {
            return html`
                <div class="app-card danger">
                    <h6>Delete ${app.name} <code class="text-muted small">${app.slug}</code>?</h6>
                    <p class="small mb-2">
                        This takes everything it serves off the CDN and deletes its pins. It can't be undone: every consumer that builds URLs on
                        <code>${app.cdn_id}</code> loses its art. Type <strong>${app.slug}</strong> to confirm.
                    </p>
                    <div class="d-flex gap-2">
                        <input type="text" class="form-control form-control-sm" style="max-width: 220px;" .value="${this._confirmSlug}"
                            @input="${(e) => (this._confirmSlug = e.target.value)}">
                        <button class="btn btn-danger btn-sm" ?disabled="${this._saving || this._confirmSlug !== app.slug}"
                            @click="${this._confirmDelete}">Delete app</button>
                        <button class="btn btn-outline-secondary btn-sm"
                            @click="${() => {
                                this._deleting = null;
                                this._confirmSlug = '';
                            }}">Cancel</button>
                    </div>
                </div>
            `;
        }
        return html`
            <div class="app-card d-flex justify-content-between align-items-start">
                <div>
                    <h6>${app.name} <code class="text-muted small">${app.slug}</code></h6>
                    <div class="small text-muted">CDN id <code>${app.cdn_id}</code></div>
                </div>
                <div class="d-flex gap-2">
                    <button class="btn btn-outline-primary btn-sm" @click="${() => (this._editing = { id: app.id, slug: app.slug, name: app.name })}">Rename</button>
                    <button class="btn btn-outline-danger btn-sm" @click="${() => (this._deleting = app)}">Delete</button>
                </div>
            </div>
        `;
    }
}

customElements.define('staff-apps-view', StaffAppsView);
